import {useState, useCallback} from 'react'
import {useDispatch} from 'react-redux'
import {createResource} from 'redux-json-api'

const ERROR_MESSAGES = {
  breached: 'This password appears in a public database of breached passwords. Please choose a different password.',
  too_common: 'Password is too common. Please choose a more unique password.',
  matches_username: 'Password cannot be the same as your email address.',
  blocked_word: 'This password is not allowed. Please choose a different password.',
  password_reused: 'Password has been used recently. Please choose a different password.',
  same_character_repeated: 'Password cannot be the same character repeated.',
  too_short: 'Password is too short. Minimum length is 12 characters.',
  too_long: 'Password is too long. Maximum length is 128 characters.',
  too_only_whitespace: 'Password cannot contain only spaces.'
}

const humanizeErrorCode = code => {
  const normalized = typeof code === 'string' ? code : String(code ?? 'validation issue')
  return `Password ${normalized.replace(/_/g, ' ')}.`
}

/**
 * Hook for server-side password validation against HIBP breach database,
 * weak password list, username match, blocked words, and other checks via
 * the v2 password-validations endpoint.
 *
 * Known error codes are mapped to user-friendly messages. Unrecognized
 * codes from the backend are humanized automatically (underscores replaced
 * with spaces) so new server-side validations surface without a frontend change.
 *
 * @returns {{ validatePassword: (password: string, email?: string) => Promise<string[]>, validating: boolean }}
 */
const usePasswordValidation = () => {
  const dispatch = useDispatch()
  const [validating, setValidating] = useState(false)

  const validatePassword = useCallback(async (password, email) => {
    setValidating(true)
    try {
      const attributes = {password}
      if (email) attributes.email = email
      const response = await dispatch(
        createResource({type: 'password-validations', attributes})
      )

      const responseAttributes = response.data.attributes || {}
      const validationErrors = responseAttributes['validation-errors'] || []

      return validationErrors
        .map(code => ERROR_MESSAGES[code] || humanizeErrorCode(code))
    } catch {
      // If the validation endpoint fails, don't block form submission.
      // Server-side validation will catch issues during the actual submit.
      return []
    } finally {
      setValidating(false)
    }
  }, [dispatch])

  return {validatePassword, validating}
}

export default usePasswordValidation
